import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import { formService } from '../../../services/formService';

const ContactForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    projectType: '',
    budgetRange: '',
    message: '',
    preferredContact: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const countryOptions = [
    { value: 'nigeria', label: 'Nigeria (+234)' },
    { value: 'cameroon', label: 'Cameroon (+237)' },
    { value: 'south-africa', label: 'South Africa (+27)' },
    { value: 'other', label: 'Other Country' }
  ];

  const projectTypeOptions = [
    { value: 'web-development', label: 'Web Development' },
    { value: 'mobile-development', label: 'Mobile App Development' },
    { value: 'it-consulting', label: 'IT Consulting' },
    { value: 'business-branding', label: 'Business Branding' },
    { value: 'mentorship', label: 'Mentorship Program' },
    { value: 'creative-direction', label: 'Creative Direction' },
    { value: 'other', label: 'Other Services' }
  ];

  const budgetRangeOptions = [
    { value: 'under-5k', label: 'Under $5,000' },
    { value: '5k-10k', label: '$5,000 - $10,000' },
    { value: '10k-25k', label: '$10,000 - $25,000' },
    { value: '25k-50k', label: '$25,000 - $50,000' },
    { value: 'over-50k', label: 'Over $50,000' },
    { value: 'flexible', label: 'Flexible Budget' }
  ];

  const contactMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'video-call', label: 'Video Call' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.name?.trim()) {
        newErrors.name = 'Full name is required';
      }
      if (!formData?.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Valid email address is required';
      }
      if (!formData?.phone?.trim() || !/^\+?[\d\s-()]+$/?.test(formData?.phone)) {
        newErrors.phone = 'Valid phone number is required';
      }
      if (!formData?.country) {
        newErrors.country = 'Please select your country';
      }
    }

    if (step === 2) {
      if (!formData?.projectType) {
        newErrors.projectType = 'Please select a project type';
      }
      if (!formData?.budgetRange) {
        newErrors.budgetRange = 'Please select a budget range';
      }
    }

    if (step === 3) {
      if (!formData?.message?.trim() || formData?.message?.trim()?.length < 20) {
        newErrors.message = 'Please provide at least 20 characters describing your project';
      }
      if (!formData?.preferredContact) {
        newErrors.preferredContact = 'Please select your preferred contact method';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateStep(3)) {
      return;
    }

    setLoading(true);

    try {
      await formService?.submitContactForm(formData);
      setSuccess(true);

      setTimeout(() => {
        setSuccess(false);
        setCurrentStep(1);
        setFormData({
          name: '',
          email: '',
          phone: '',
          country: '',
          projectType: '',
          budgetRange: '',
          message: '',
          preferredContact: ''
        });
      }, 5000);
    } catch (error) {
      setErrors({ submit: error?.message || 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-card rounded-xl p-6 md:p-8 lg:p-12 shadow-lg border border-border">
        <div className="flex flex-col items-center justify-center text-center py-8 md:py-12">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-success/10 flex items-center justify-center mb-4 md:mb-6">
            <Icon name="CheckCircle2" size={40} color="var(--color-success)" />
          </div>
          <h3 className="text-2xl md:text-3xl font-semibold mb-3 md:mb-4">Message Sent Successfully!</h3>
          <p className="text-muted-foreground text-sm md:text-base mb-6 md:mb-8 max-w-md">
            Thank you for reaching out. I'll review your inquiry and get back to you within 24-48 hours via your preferred contact method.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 md:gap-4 w-full sm:w-auto">
            <Button
              variant="default"
              onClick={() => setSuccess(false)}
              iconName="Send"
              iconPosition="left"
            >
              Send Another Message
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.href = '/home-landing'}
              iconName="Home"
              iconPosition="left"
            >
              Back to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl p-6 md:p-8 lg:p-12 shadow-lg border border-border">
      <div className="mb-6 md:mb-8">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-3 md:mb-4">Get In Touch</h2>
        <p className="text-muted-foreground text-sm md:text-base">
          Fill out the form below and I'll respond within 24-48 hours
        </p>
      </div>
      <div className="mb-6 md:mb-8">
        <div className="flex items-center justify-between">
          {[1, 2, 3]?.map((step) => (
            <React.Fragment key={step}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center font-semibold transition-all ${
                    currentStep >= step
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {currentStep > step ? (
                    <Icon name="Check" size={20} color="currentColor" />
                  ) : (
                    step
                  )}
                </div>
                <span className="text-xs md:text-sm mt-2 text-muted-foreground hidden sm:block">
                  {step === 1 ? 'Personal' : step === 2 ? 'Project' : 'Details'}
                </span>
              </div>
              {step < 3 && (
                <div
                  className={`flex-1 h-1 mx-2 md:mx-4 rounded transition-all ${
                    currentStep > step ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        {currentStep === 1 && (
          <div className="space-y-4 md:space-y-6">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter your full name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="your.email@example.com"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />

            <Input
              label="Phone Number"
              type="tel"
              placeholder="+234 XXX XXX XXXX"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
              error={errors?.phone}
              required
            />

            <Select
              label="Country/Region"
              placeholder="Select your country"
              options={countryOptions}
              value={formData?.country}
              onChange={(value) => handleInputChange('country', value)}
              error={errors?.country}
              required
            />

            <Button
              type="button"
              variant="default"
              onClick={handleNext}
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
            >
              Continue to Project Details
            </Button>
          </div>
        )}

        {currentStep === 2 && (
          <div className="space-y-4 md:space-y-6">
            <Select
              label="Project Type"
              placeholder="Select the type of service you need"
              options={projectTypeOptions}
              value={formData?.projectType}
              onChange={(value) => handleInputChange('projectType', value)}
              error={errors?.projectType}
              required
            />

            <Select
              label="Budget Range"
              placeholder="Select your budget range"
              options={budgetRangeOptions}
              value={formData?.budgetRange}
              onChange={(value) => handleInputChange('budgetRange', value)}
              error={errors?.budgetRange}
              required
            />

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                fullWidth
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back
              </Button>
              <Button
                type="button"
                variant="default"
                onClick={handleNext}
                fullWidth
                iconName="ArrowRight"
                iconPosition="right"
              >
                Continue to Message
              </Button>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="space-y-4 md:space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2">
                Project Description <span className="text-error">*</span>
              </label>
              <textarea
                className="w-full min-h-[120px] md:min-h-[150px] px-4 py-3 rounded-lg bg-input border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Tell me about your project, goals, timeline, and any specific requirements..."
                value={formData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
              />
              {errors?.message && (
                <p className="text-sm text-error mt-1">{errors?.message}</p>
              )}
            </div>

            <Select
              label="Preferred Contact Method"
              placeholder="How would you like me to reach you?"
              options={contactMethodOptions}
              value={formData?.preferredContact}
              onChange={(value) => handleInputChange('preferredContact', value)}
              error={errors?.preferredContact}
              required
            />

            <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleBack}
                fullWidth
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back
              </Button>
              <Button
                type="submit"
                variant="default"
                loading={loading}
                fullWidth
                iconName="Send"
                iconPosition="right"
              >
                Submit Inquiry
              </Button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ContactForm;